function isEditableDragTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return Boolean(
    target.closest("input, textarea, select, option, [contenteditable='true']"),
  );
}

export function isColumnResizeHandleTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('.email-builder-column-resize-handle') !== null
  );
}

export function isImageResizeHandleTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('.email-builder-image-resize-handle') !== null
  );
}

function isResizeInteractionTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  return isColumnResizeHandleTarget(target) || isImageResizeHandleTarget(target);
}

let lastPointerDownTarget: EventTarget | null = null;

if (typeof globalThis.window !== 'undefined') {
  globalThis.window.addEventListener(
    'pointerdown',
    event => {
      lastPointerDownTarget = event.target;
    },
    { capture: true },
  );
}

function isPointerOverEmailBuilderDragChrome(
  element: Element,
): boolean {
  return (
    element.closest('.builder-toolbar-menu-panel.open') !== null ||
    element.closest('.builder-toolbar-rail') !== null ||
    element.closest('[data-editor-region]') !== null ||
    element.closest('.email-builder-settings-side-panel.open') !== null ||
    element.closest('.ui-bottom-sheet-dialog-popup') !== null
  );
}

export function isPointerInEmailBuilderCanvasDropZone(
  pointer: { x: number; y: number },
): boolean {
  if (typeof globalThis.document === 'undefined') {
    return false;
  }

  const topElement = globalThis.document.elementFromPoint(pointer.x, pointer.y);
  if (!(topElement instanceof Element)) {
    return false;
  }

  if (isPointerOverEmailBuilderDragChrome(topElement)) {
    return false;
  }

  return topElement.closest('#main-canvas') !== null;
}

export function getLastPointerDownTarget(): EventTarget | null {
  return lastPointerDownTarget;
}

export function isCanvasDragActive(): boolean {
  if (typeof globalThis.document === 'undefined') {
    return false;
  }
  return (
    globalThis.document.getElementById('main-canvas')?.getAttribute(
      'data-canvas-drag-active',
    ) === 'true'
  );
}

export function isFrameLabelDragHandle(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  const labelHit = target.closest('.email-builder-canvas-frame__label-hit');
  if (labelHit === null) {
    return false;
  }
  if (
    target.closest(
      'button.email-builder-canvas-frame__chip, button.email-builder-canvas-frame__expand',
    ) !== null
  ) {
    return false;
  }
  return true;
}

export function isPointerOnDragItemFrameLabel(
  target: EventTarget | null,
  level: 'block' | 'row' | 'group',
  entityId: string,
): boolean {
  if (!isFrameLabelDragHandle(target)) {
    return false;
  }
  if (!(target instanceof Element)) {
    return false;
  }
  const frame = target.closest(
    `.email-builder-canvas-frame[data-level="${level}"]`,
  );
  return frame?.getAttribute('data-entity-id') === `${level}:${entityId}`;
}

export function canStartEditorDrag(
  optionsCanDrag: boolean | undefined,
): boolean {
  if (optionsCanDrag === false) {
    return false;
  }
  if (isFrameLabelDragHandle(lastPointerDownTarget)) {
    return true;
  }
  if (
    lastPointerDownTarget instanceof Element &&
    lastPointerDownTarget.closest('[data-slot-index]')
  ) {
    return false;
  }
  if (isResizeInteractionTarget(lastPointerDownTarget)) {
    return false;
  }
  return !isEditableDragTarget(lastPointerDownTarget);
}

export function isPointerDownInsideBlockFrame(): boolean {
  if (!(lastPointerDownTarget instanceof Element)) {
    return false;
  }
  return lastPointerDownTarget.closest('[data-level="block"]') !== null;
}

export function canStartRowGroupDrag(
  optionsCanDrag: boolean | undefined,
  ownLevel: 'row' | 'group',
  allowInsideNestedContent = false,
  ownEntityId?: string,
): boolean {
  if (!canStartEditorDrag(optionsCanDrag)) {
    return false;
  }
  if (!(lastPointerDownTarget instanceof Element)) {
    return false;
  }
  if (isFrameLabelDragHandle(lastPointerDownTarget)) {
    if (ownEntityId !== undefined) {
      return isPointerOnDragItemFrameLabel(
        lastPointerDownTarget,
        ownLevel,
        ownEntityId,
      );
    }
    return true;
  }
  if (!allowInsideNestedContent && isPointerDownInsideBlockFrame()) {
    return false;
  }
  const innerDraggable = lastPointerDownTarget.closest(
    '[data-draggable="true"][data-level]',
  );
  if (innerDraggable instanceof Element) {
    const innerLevel = innerDraggable.getAttribute('data-level');
    if (!allowInsideNestedContent && innerLevel === 'block') {
      return false;
    }
    if (
      !allowInsideNestedContent &&
      ownLevel === 'group' &&
      innerLevel === 'row'
    ) {
      return false;
    }
  }
  return true;
}
